import {
  ContainerService,
  ContainerStatusUpdate,
} from "@/app/services/generated/containerService";
import { EnumContainerStatus } from "../generated/enums/enumContainerStatus.generated";
import type Guid from "@/app/types/common/guid";
import { useI18n } from "@/plugins/i18n";
import { BrewManError } from "@/app/errors";

const { $m, $t } = useI18n();

export default class ContainerServiceExtended extends ContainerService {
  getValidNewContainerStatuses(status: EnumContainerStatus) {
    let validStatuses: EnumContainerStatus[] = [
      EnumContainerStatus.fullAndIn,
      EnumContainerStatus.out,
    ];
    if (status !== EnumContainerStatus.lost) {
      validStatuses.push(EnumContainerStatus.lost);
    }
    if (status !== EnumContainerStatus.cleanAndIn) {
      validStatuses.push(EnumContainerStatus.cleanAndIn);
    }
    if (status !== EnumContainerStatus.dirtyAndIn) {
      validStatuses.push(EnumContainerStatus.dirtyAndIn);
    }
    if (status !== EnumContainerStatus.outOfOrder) {
      validStatuses.push(EnumContainerStatus.outOfOrder);
    }
    return validStatuses;
  }

  async fillContainers(
    tenantId: Guid,
    permLabels: readonly string[],
    productBatchId: Guid,
    assemblyId: Guid | null,
    processId: Guid | null
  ) {
    const containers = await this.getContainersByPermLabels(
      tenantId,
      permLabels
    );

    if (!permLabels.every((pl) => containers.some((c) => c.permLabel === pl))) {
      // Missing container for a label
      throw new BrewManError($t($m.keys.containers_not_found).toString());
    }

    const containerUpdates = containers.map((c) => {
      const newStatus = new ContainerStatusUpdate();
      newStatus.containerId = c.id;
      newStatus.productBatchId = productBatchId;
      newStatus.filledFromAssemblyId = assemblyId;
      newStatus.isIn = true;
      newStatus.filledFromProcessId = processId;

      return newStatus;
    });

    if (containerUpdates.length > 0) {
      await this.updateContainerStatuses(tenantId, containerUpdates);
    }
  }

  async emptyContainers(tenantId: Guid, permLabels: readonly string[]) {
    const containers = await this.getContainersByPermLabels(
      tenantId,
      permLabels
    );

    if (!permLabels.every((pl) => containers.some((c) => c.permLabel === pl))) {
      // Missing container for a label
      throw new BrewManError($t($m.keys.containers_not_found).toString());
    }

    const containerUpdates = containers.map((c) => {
      const newStatus = new ContainerStatusUpdate();
      newStatus.containerId = c.id;
      newStatus.productBatchId = null;
      newStatus.filledFromAssemblyId = null;
      newStatus.isIn = true;
      newStatus.needsCleaning = true;
      newStatus.filledFromProcessId = null;

      return newStatus;
    });

    if (containerUpdates.length > 0) {
      await this.updateContainerStatuses(tenantId, containerUpdates);
    }
  }
}

export const containerService = new ContainerServiceExtended();
